import React from 'react';
import { styles } from './CouponProgressBanner.css';
import { PerksContainer } from '../PerksContainer';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import CloseIcon from '@jbhi-fi/lanyard-ui/components/Icons/Close';
import { Icon } from '@jbhi-fi/lanyard-ui/components/Icon';
import Alert from '@jbhi-fi/lanyard-ui/components/Alert';
import { ProgressBar } from '../ProgressBar';
import { palette } from '@jbhi-fi/lanyard-ui/themes';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';

type BaseProps = {
  title: string;
  expiryDate: string;
  hasWarning: boolean;
  warningText?: string;
  isApplied?: boolean;
  discount?: number;
  value?: number;
  targetValue?: number;
};

type DefaultVariantProps = BaseProps & {
  variant?: 'default';
  onApplyCoupon: () => void;
  onClose: () => void;
};

type MiniVariantProps = BaseProps & {
  variant: 'mini';
  onApplyCoupon?: () => void;
  onClose?: () => void;
};

type CouponProgressBannerProps = DefaultVariantProps | MiniVariantProps;

const WARNING_MESSAGE = 'Product excluded from Spend & Get offer';
const formatPrice = (value: number): string => {
  return Number.isInteger(value) ? value.toString() : value.toFixed(2);
};

const TermsAndConditionsButton = ({ onApplyCoupon }: { onApplyCoupon: () => void }) => {
  return (
    <Button classes={styles.termsAndConditions} onClick={onApplyCoupon} type="link">
      View T&Cs & exclusions
    </Button>
  );
};

const TargetMessage = ({
  variant,
  diff,
  discount,
}: {
  variant: 'default' | 'mini';
  diff: number;
  discount: number;
}) => {
  return (
    <p className={styles.targetMessage({ variant })}>
      {diff === 0
        ? `You've unlocked $${discount} off your order!`
        : `You're $${formatPrice(diff)} away from $${discount} off your order`}
    </p>
  );
};

const MiniAppliedBanner = ({
  diff,
  discount,
  value,
  targetValue,
}: {
  diff: number;
  discount: number;
  value?: number;
  targetValue?: number;
}) => {
  return (
    <div className={styles.content({ isApplied: true, variant: 'mini' })}>
      <TargetMessage variant="mini" diff={diff} discount={discount} />
      {targetValue ? (
        <ProgressBar
          currentItemsCount={value ?? 0}
          totalItemsCount={targetValue}
          icon={<Icon name="PerksCoupon" fill={palette.primary.main} />}
          size="medium"
        />
      ) : null}
    </div>
  );
};

export const CouponProgressBanner = ({
  variant = 'default',
  title,
  expiryDate,
  hasWarning = false,
  isApplied,
  discount,
  value,
  targetValue,
  onApplyCoupon,
  onClose,
}: CouponProgressBannerProps) => {
  const shouldShowProgress = isApplied && targetValue;
  const diff = shouldShowProgress ? Math.max(targetValue - (value ?? 0), 0) : null;
  const isMobile = useBreakpoint('md', 'down');

  if (variant === 'mini' && isApplied)
    return <MiniAppliedBanner diff={diff ?? 0} discount={discount ?? 0} value={value} targetValue={targetValue} />;

  if (!onApplyCoupon || !onClose) return null;
  return (
    <PerksContainer padding="small">
      <div className={styles.content({ isApplied, hasWarning })}>
        {isApplied && targetValue ? (
          <>
            <div className={styles.appliedHeader}>
              <Icon name="PerksLogoIllustrated" height={27} width={97} style={{ gridArea: 'perksLogo' }} />
              <div className={styles.targetMessage({ variant })}>
                <TargetMessage variant={variant} diff={diff ?? 0} discount={discount ?? 0} />
              </div>
              <Button classes={styles.closeButton} type="iconOnly" onClick={onClose} icon={<CloseIcon />} />
            </div>
            <ProgressBar
              className={styles.progressBar}
              currentItemsCount={value ?? 0}
              totalItemsCount={targetValue}
              icon={<Icon name="PerksCoupon" fill={palette.primary.main} />}
              size="medium"
              shouldShowCompleteIcon
            />
            {hasWarning ? (
              <Alert
                showIcon
                type="warning"
                heading={WARNING_MESSAGE}
                classes={styles.alert}
                content={<TermsAndConditionsButton onApplyCoupon={onApplyCoupon} />}
              />
            ) : (
              <div className={styles.appliedFooter}>
                {!isMobile ? <p>Ends {expiryDate}</p> : null}
                {!hasWarning ? <TermsAndConditionsButton onApplyCoupon={onApplyCoupon} /> : null}
              </div>
            )}
          </>
        ) : (
          <>
            <div className={styles.couponContent}>
              <div className={styles.logoContainer}>
                {isMobile ? (
                  <Icon name="PerksMascotIllustrated" height={54} width="auto" />
                ) : (
                  <>
                    <Icon name="PerksCouponIllustrated" height={27} width={28} />
                    <Icon name="PerksLogoIllustrated" height={27} width={97} />
                  </>
                )}
              </div>
              <div className={styles.couponInformation}>
                <CalloutFont as="h3">{title}</CalloutFont>
                <div className={styles.termsAndExpiryWrapper}>
                  <p>Ends {expiryDate}</p>
                  <TermsAndConditionsButton onApplyCoupon={onApplyCoupon} />
                </div>
              </div>
            </div>
            <div className={styles.actions}>
              <Button classes={styles.closeButton} type="iconOnly" onClick={onClose} icon={<CloseIcon />} />
              <Button classes={styles.applyButton} onClick={onApplyCoupon} type="primary">
                Apply coupon
              </Button>
            </div>
          </>
        )}
      </div>
      {!isMobile ? (
        <Button classes={styles.closeButton} type="iconOnly" onClick={onClose} icon={<CloseIcon />} />
      ) : null}
    </PerksContainer>
  );
};
